import * as React from 'react';
import { getAuthToken, isUserLoggedIn } from '@/redux/modules/user';
import { getIsWhiteLabel, getWhiteLabelPrimaryColor } from '@/redux/modules/whiteLabel';
import styled, { ThemeProvider } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import AppHeaderMeta from './AppHeaderMeta';
import appTheme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import Auth from './Auth';
import BackToTop from '@/components/BacktoTop/BackToTop';
import BannerOutdatedBrowser from '@/components/BannerOutdatedBrowser/BannerOutdatedBrowser';
import chroma from 'chroma-js';
import CurrencySelectionPopover from '@/components/CurrencySelection/CurrencySelectionPopover';
import DismissableGetMobileBanner from '@/components/DismissableGetMobileBanner/DismissableGetMobileBanner';
import DismissableGetWebNotificationsBanner from '@/components/DismissableGetWebNotificationsBanner/DismissableGetWebNotificationsBanner';
import FeedbackLink from '@/components/FeedbackLink/FeedbackLink';
import Footer from '@/components/Footer/Footer';
import ForgotPasswordAction from '@/components/Modals/ForgotPassword/ForgotPasswordAction';
import GeoLocationSearchExperimentContainer from '@/components/ExperimentSetup/GeoLocationSearchExperimentContainer';
import { getCookie } from '@/redux/modules/cookies';
import { getPageSizeFromCookie } from '@/utils/pagination';
import Header from '@/components/Header/Header';
import { hydrateClientState } from '@/redux/modules/client';
import LogRocket from '@/components/LogRocket/LogRocket';
import Modals from '@/components/Modals/Modals';
import NewSafariSentinel from '@/components/NewSafariSentinel/NewSafariSentinel';
import NotificationCountTitle from '@/components/NotificationCountTitle/NotificationCountTitle';
import { parseQueryParams } from '@/utils/queryParams';
import PubnubBidderNotificationContainer from '@/components/PubnubContainer/PubnubBidderNotificationContainer';
import PubnubGlobalNotificationContainer from '@/components/PubnubContainer/PubnubGlobalNotificationContainer';
import SailthruInit from '@/components/Sailthru/SailthruInit';
import SegmentTracker from '@/components/Segment/SegmentTracker';
import Signifyd from '@/components/Signifyd/Signifyd';
import SignupAction from '@/components/Modals/SignUp/SignupAction';
import SuspendedBidderBanner from '@/components/SuspendedBidderBanner/SuspendedBidderBanner';
import TermsOfServiceBanner from '@/components/TermsOfServiceBanner/TermsOfServiceBanner';
import ToastMessage from '@liveauctioneers/caterwaul-components/lib/ToastMessage/ToastMessage';
import TrustBoxScript from '@/components/TrustBox/TrustBoxScript';
import { useLocation } from 'react-router-dom';
import WindowSizeContainer from '@/components/WindowSizeContainer/WindowSizeContainer';

type Props = {
    children: React.ReactNode;
};

const App = ({ children }: Props) => {
    const { pathname, search } = useLocation();
    const dispatch = useDispatch();
    const [isClientHydrated, setClientHydrated] = React.useState(false);
    const authToken = useSelector(getAuthToken);
    const { hideForTesting }: any = parseQueryParams(search);
    const userLoggedIn = useSelector(isUserLoggedIn);
    const isBiddingConsole = pathname.includes('/console/');
    const isCheckout = pathname.includes('/checkout/');
    const isWhiteLabel = useSelector(getIsWhiteLabel);
    const whiteLabelPrimaryColor = useSelector(getWhiteLabelPrimaryColor);

    const token = useSelector((state) => getCookie(state, 'bidder-auth'));
    const hasLoggedInBefore =
        Boolean(useSelector((state) => getCookie(state, 'bidder-has-logged-in'))) || Boolean(token);
    const dateLastShown = useSelector((state) => getCookie(state, 'join-modal-last-seen')) || '';
    const pageSizeCookie = Number(useSelector((state) => getCookie(state, 'pagination-page-size'))) || 24;

    const hydrateClient = React.useCallback(async () => {
        const pageSize = getPageSizeFromCookie(pageSizeCookie);
        const disableModalForTesting = isBiddingConsole || hideForTesting === 'true';

        dispatch(
            hydrateClientState({
                dateLastShown,
                disableModalForTesting,
                hasLoggedInBefore,
                pageSize,
            })
        );
    }, [dateLastShown, dispatch, hasLoggedInBefore, hideForTesting, isBiddingConsole, pageSizeCookie]);

    React.useEffect(() => {
        if (!isClientHydrated) {
            hydrateClient().then(() => {
                setClientHydrated(true);
            });
        }

        return () => {
            window.onstorage = null;
        };
    }, [authToken, dispatch, hydrateClient, isClientHydrated]);

    let newTheme = appTheme;
    if (isWhiteLabel && whiteLabelPrimaryColor && whiteLabelPrimaryColor !== appTheme.colors.blue100) {
        newTheme = {
            ...appTheme,
            colors: {
                ...appTheme.colors,
                blue100: chroma(whiteLabelPrimaryColor).hex(),
                blue200: chroma(whiteLabelPrimaryColor).darken().hex(),
                blue300: chroma(whiteLabelPrimaryColor).brighten().hex(),
            },
        };
    }

    const showHeaderAndFooter = !isBiddingConsole && !isCheckout;

    const appJsx = (
        <StyledApp>
            <WindowSizeContainer /> {/** TODO: update this */}
            <AppHeaderMeta />
            <SailthruInit />
            <BannerOutdatedBrowser />
            {showHeaderAndFooter && <Header />}
            <StyledAppContent id="main">{children}</StyledAppContent>
            {showHeaderAndFooter && <Footer />}
            <Modals />
            <CurrencySelectionPopover />
            <SegmentTracker />
            <BackToTop />
        </StyledApp>
    );

    return (
        <React.StrictMode>
            <Signifyd />
            <TrustBoxScript />
            <GeoLocationSearchExperimentContainer />
            <LogRocket />
            <FeedbackLink />
            {userLoggedIn && <PubnubBidderNotificationContainer />}
            <TermsOfServiceBanner />
            <DismissableGetMobileBanner />
            {userLoggedIn && <DismissableGetWebNotificationsBanner />}
            <SuspendedBidderBanner />
            {/* <CustomToastContainer /> */}
            <ToastMessage />
            <PubnubGlobalNotificationContainer />
            <NotificationCountTitle /> {/** TODO: update this */}
            <NewSafariSentinel />
            <Auth />
            {isClientHydrated && <SignupAction />}
            {isClientHydrated && <ForgotPasswordAction />}
            {isWhiteLabel ? <ThemeProvider theme={newTheme}>{appJsx}</ThemeProvider> : appJsx}
        </React.StrictMode>
    );
};

export default React.memo(App);

const StyledApp = styled.div`
    height: 100%;
    width: 100%;
    position: relative;

    @media print {
        overflow: initial;
    }
`;

const StyledAppContent = styled.div`
    height: 100%;

    @media (min-width: 1240px) {
        margin: 0 auto;
    }

    @media print {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        bottom: 0;
        margin: auto;
        margin-top: 0 !important;
    }
`;
